export type GraphicsAccelStatus = "on" | "off" | "unknown";

export interface GraphicsAccelReport {
  status: GraphicsAccelStatus;
  /** UNMASKED_RENDERER_WEBGL, when the browser provides it. */
  renderer: string | null;
}

const SOFTWARE_RENDERERS = ["swiftshader", "llvmpipe", "software", "microsoft basic render"];

export function isSoftwareRenderer(renderer: string): boolean {
  const name = renderer.toLowerCase();
  return SOFTWARE_RENDERERS.some((marker) => name.includes(marker));
}

/**
 * `caveatOk` is true when a WebGL context was created with
 * `failIfMajorPerformanceCaveat`. A software renderer string, or a context
 * that exists only without that flag, is Off. A fast context with no
 * renderer string stays Unknown.
 */
export function classifyGraphicsAcceleration(
  renderer: string | null,
  caveatOk: boolean,
): GraphicsAccelReport {
  const name = renderer?.trim() || null;
  if (name && isSoftwareRenderer(name)) return { status: "off", renderer: name };
  if (!caveatOk) return { status: "off", renderer: name };
  if (!name) return { status: "unknown", renderer: null };
  return { status: "on", renderer: name };
}

/** Probe the real WebGL implementation. Loses the probe context so the map can keep its own. */
export function readGraphicsAcceleration(): GraphicsAccelReport {
  if (typeof document === "undefined") return { status: "unknown", renderer: null };
  const fast = rendererFrom(true);
  if (fast.created) return classifyGraphicsAcceleration(fast.renderer, true);
  const plain = rendererFrom(false);
  if (plain.created) return classifyGraphicsAcceleration(plain.renderer, false);
  return { status: "unknown", renderer: null };
}

function rendererFrom(caveat: boolean): { created: boolean; renderer: string | null } {
  const canvas = document.createElement("canvas");
  const attrs: WebGLContextAttributes = { powerPreference: "high-performance" };
  if (caveat) attrs.failIfMajorPerformanceCaveat = true;
  const gl = canvas.getContext("webgl2", attrs) ?? canvas.getContext("webgl", attrs);
  if (!gl) return { created: false, renderer: null };
  try {
    return { created: true, renderer: unmaskedRenderer(gl) };
  } finally {
    gl.getExtension("WEBGL_lose_context")?.loseContext();
  }
}

function unmaskedRenderer(gl: WebGLRenderingContext | WebGL2RenderingContext): string | null {
  const info = gl.getExtension("WEBGL_debug_renderer_info");
  if (!info) return null;
  const value = gl.getParameter(info.UNMASKED_RENDERER_WEBGL);
  return typeof value === "string" ? value : null;
}
